'use client';

import { cn } from '@ps/ui/utils';
import { Eye, EyeOff, Star } from 'lucide-react';

import type { CarouselMedia } from '../../pdp/components/ProductImageCarousel';

/**
 * Favorite + visibility chips that overlay the active carousel
 * slide when it's a creator's own AI-generated media. Mirrors the
 * RN `ListingImageCarouselAffiliateOverlay` chips that sit at the
 * top-left of the hero — owner-only, only when `slide.tag ===
 * 'avatar-me'`.
 *
 * Clicks are wired to the optimistic-toggle handlers in
 * `PdpWearWithAiSlots`; the chip just renders state + fires the
 * callbacks.
 */
export function WearWithAiSlideChips({
  slide,
  onToggleFavorite,
  onToggleVisibility,
  pending,
}: {
  slide: CarouselMedia;
  onToggleFavorite: () => void;
  onToggleVisibility: () => void;
  /** Disable the chips while a toggle is in-flight to prevent
   *  rapid-fire double-fires. */
  pending?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        aria-label={slide.favorite ? 'Unfavorite' : 'Favorite'}
        onClick={onToggleFavorite}
        disabled={pending}
        className={cn(
          'bg-surface-2 text-brand hover:bg-surface-3 text-label flex items-center gap-1 rounded-md px-2 py-1 backdrop-blur-sm transition',
          pending && 'cursor-not-allowed opacity-60',
        )}
      >
        <Star
          className="size-4"
          fill={slide.favorite ? '#7DFFB3' : 'transparent'}
        />
      </button>
      <button
        type="button"
        aria-label={slide.visible ? 'Hide' : 'Show'}
        onClick={onToggleVisibility}
        disabled={pending}
        className={cn(
          'bg-surface-2 text-brand hover:bg-surface-3 text-label flex items-center gap-1 rounded-md px-2 py-1 font-medium backdrop-blur-sm transition',
          pending && 'cursor-not-allowed opacity-60',
        )}
      >
        {slide.visible ? (
          <Eye className="size-4" />
        ) : (
          <EyeOff className="size-4" />
        )}
        <span>{slide.visible ? 'Hide' : 'Show'}</span>
      </button>
    </div>
  );
}
